import { alpha, Box, IconButton, Stack, styled, Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
import { ConversationChannelTypeExtended } from '@provision/chat-utils';

import {
  ChannelIdentifiers,
  ConversationChannelType,
  ConversationDraftScope,
  ConversationDraftSideEffectActionType,
  ConversationParticipantEntity,
  ConversationParticipationMode,
  ConversationStatus,
} from '@provision/conversation-types';
import { ConversationParticipation, EntityIdentifier } from '@provision/entity-types';
import { renderRichTextDocumentToHtml } from '@provision/formatting-utils';
import { useApolloClient, useMutation } from '@provision/graphql-client';
import {
  Editor,
  getAttachmentsFromEditor,
  isConversationAttachmentWithUrl,
  isFile,
  MentionsPopupState,
  RichTextEditorFile,
} from '@provision/rich-text-editor';
import dayjs from 'dayjs';
import {
  Dispatch,
  forwardRef,
  MutableRefObject,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { isNullish } from 'remeda';
import { Subject } from 'rxjs';
import { DynamicIcon, ShortcutText } from 'src/components';
import { MentionPopup } from 'src/components/MentionPopup/mention-popup';
import MessageTypeSelector, {
  MessageTypeSelectorProps,
} from 'src/components/MessageTypeSelector/message-type-selector';
import { useRegisterQuickActions } from 'src/components/QuickActions/use-register-quick-actions';
import RichTextEditor from 'src/components/RichTextEditor/rich-text-editor';
import { appShortcuts } from 'src/constants/appShortcuts';
import { GLASS_BLUR } from 'src/constants/glass';
import {
  Conversation,
  ConversationParticipantItem,
  DraftAttachmentInput,
  isDraftNonEmpty,
  Macro,
  MacroActionRule,
  PreparedConversationParticipantOption,
  useConversationMessageDraft,
  useCurrentUserProfile,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { NO_SUBJECT_PLACEHOLDER } from 'src/graphql/entities/conversation/conversation.mappings';
import {
  SubmitConversationDataChannel,
  SubmitConversationDataParticipant,
} from 'src/graphql/entities/conversation/conversation.types';
import {
  DELETE_CONVERSATION_MESSAGE_DRAFT,
  GET_CONVERSATION_MESSAGE_DRAFT_QUERY,
} from 'src/graphql/entities/conversation-message-draft/conversation-message-draft.operations';
import type { GetUserConversationMessageDraft } from 'src/graphql/entities/conversation-message-draft/hooks/use-conversation-message-draft';
import type { DeleteConversationMessageDraft } from 'src/graphql/entities/conversation-message-draft/hooks/use-delete-conversation-message-draft';
import {
  findServerDraftIdByScope,
  isOptimisticDraftId,
} from 'src/graphql/entities/conversation-message-draft/hooks/utils';
import { useFileUpload } from 'src/graphql/entities/files-upload';

import { resolveMacroSnippetSource } from 'src/graphql/entities/macro/macro.mappings';
import { MacroSnippetSource } from 'src/graphql/entities/macro/macro.types';
import { AiAssistantTrigger } from 'src/pages/Conversations/Chat/AiAssistant/ai-assistant-trigger';
import type { AiGenerationRequest } from 'src/pages/Conversations/Chat/AiAssistant/ai-generation';
import { AiGenerationErrorBar } from 'src/pages/Conversations/Chat/AiAssistant/ai-generation-error-bar';
import {
  type AiMacroDraftContext,
  type AppliedMacroAction,
  isAiMacroContextForScope,
  resolveAiMacroActions,
} from 'src/pages/Conversations/Chat/AiAssistant/ai-macro-draft-context';
import { PendingAiSuggestionHeader } from 'src/pages/Conversations/Chat/AiAssistant/pending-ai-suggestion-header';
import { SuggestionGenerationLoader } from 'src/pages/Conversations/Chat/AiAssistant/suggestion-generation-loader';
import { useAiAssistant } from 'src/pages/Conversations/Chat/AiAssistant/use-ai-assistant';
import { useComposerBanners } from 'src/pages/Conversations/Chat/Banners/composer-banners-context';
import { HolidayBanner } from 'src/pages/Conversations/Chat/Banners/holiday-banner';
import {
  EditorDoc,
  readMacrosFromDoc,
} from 'src/pages/Conversations/Chat/conversation-draft-macros';
import {
  DraftSchedule,
  DraftScheduleSendAndAction,
} from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { clearLocalDraftScope } from 'src/pages/Conversations/Chat/conversation-draft-storage';
import {
  buildSmsParticipantFromStartResponse,
  findSmsParticipationForRecipient,
  findSmsSessionParticipation,
  findTwilioSmsParticipation,
  getActiveChannels,
  getPreparedParticipants,
  getSendButtonText,
  getSenderParticipant,
  hasEmailChannel,
  validateMacroActions,
} from 'src/pages/Conversations/Chat/InputField/utils';
import {
  ActionChipIcon,
  AppliedMacroActionsList,
  CustomChip,
} from 'src/pages/Conversations/Chat/Macros/applied-macro-actions-list';
import { MacrosTrigger } from 'src/pages/Conversations/Chat/Macros/macros-trigger';
import { ParticipationPanel } from 'src/pages/Conversations/Chat/ParticipationPanel/participation-panel';
import { SelectedParticipants } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { UseSelectedParticipants } from 'src/pages/Conversations/Chat/ParticipationPanel/use-selected-participants';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';

import { privateNoteButtonStyles } from 'src/pages/Conversations/Chat/shared-styles';
import {
  parseSmsSessionAlreadyOpenError,
  SmsSessionAlreadyOpenAlert,
  type StartedSmsParticipation,
  useSmsSessionContext,
} from 'src/pages/Conversations/Chat/SmsSession';
import { useConversationDraft } from 'src/pages/Conversations/Chat/use-conversation-draft';
import { useDraftCommitGuards } from 'src/pages/Conversations/Chat/use-draft-commit-guards';
import { isSameParticipantOption } from 'src/pages/Conversations/Chat/utils';
import { getConversationAttachmentUrl } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { Modals, useModalMethods } from 'src/store/modals';
import { useSelectedConversationRequesterStore } from 'src/store/selected-conversation-requester';
import {
  useConfirmationModal,
  useDisplayTime,
  useNotifications,
  useRegisterShortcuts,
} from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';
import { useScreenDimensions } from 'src/utils/system';
import { htmlToPlainText } from 'src/utils/text-parsing';
import { ComposerFormatToggleIcon } from './composer-format-toggle-icon';
import {
  COMPOSER_PINNED_FORMATTING_METHODS,
  COMPOSER_TOOLBAR_ICON_OVERRIDES,
} from './composer-toolbar-config';
import {
  applyEditorContent,
  applyMacroContent,
  buildDraftJson,
  decideEditorContent,
  editorHasContent,
  hasOpenEditorSuggestion,
  readEditorDocJson,
  stripMeta,
} from './editor-content';
import { useComposerSuggestedReply } from './hooks/use-composer-suggested-reply';
import { useDraftScheduling } from './hooks/use-draft-scheduling';
import { normalizeOutgoingMessageHtml } from './normalize-outgoing-message-html';
import ScheduledBanner from './scheduled-banner';
import SendOptionsButton from './send-options-button';
import { SmartSendModal } from './smart-send/smart-send-modal';
import { SmsChannelMode, SmsChannelSwitch, SmsLiveRecipientLine } from './sms-channel-switch';
import { SuggestedReplyBanner } from './suggested-reply-banner';

const InputFieldBackground = styled(Stack, {
  name: 'InputFieldBackground',
  shouldForwardProp: (prop) => prop !== 'isInternalNote' && prop !== 'hasBannerAbove',
})<{
  isInternalNote: boolean;
  hasBannerAbove: boolean;
}>(({ theme, isInternalNote, hasBannerAbove }) => {
  const opaqueBackgroundColor = isInternalNote
    ? theme.palette.custom.OV.custom.InternalNotes.noteInputContainer
    : theme.palette.custom.OV.sys.surfaceBright;

  const cardRadius = 12;

  const basedStyles = {
    // The card's own chrome (mode selector, recipients, dividers, tools row, paddings) never
    // fits under ~12rem — below that a banner sharing the box squeezes the tools row against
    // the dock's overflow:hidden. Same on mobile: the dock's ceiling is sized to
    // fit this floor instead of the floor being lowered.
    minHeight: '12rem',
    height: '100%',
    position: 'relative' as const,
    flexGrow: 1,
    // a banner above squares the card's top corners so the stack reads as one sheet
    borderRadius: hasBannerAbove ? `0 0 ${cardRadius}px ${cardRadius}px` : cardRadius,
    padding: theme.spacing(1),
    overflow: 'visible',
    backgroundColor: alpha(
      isInternalNote
        ? theme.palette.custom.OV.custom.InternalNotes.noteInputContainer
        : theme.palette.custom.OV.sys.surfaceContainerLowest,
      0.62,
    ),
    backdropFilter: GLASS_BLUR,
    WebkitBackdropFilter: GLASS_BLUR,
    transition: 'background-color .4s ease, border-color .4s ease',
    '&:focus-within': {
      backgroundColor: alpha(
        isInternalNote
          ? theme.palette.custom.OV.custom.InternalNotes.noteInputContainer
          : theme.palette.custom.OV.sys.surfaceContainerLowest,
        0.84,
      ),
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
      backgroundColor: opaqueBackgroundColor,
      '&:focus-within': {
        backgroundColor: opaqueBackgroundColor,
      },
    },
  };

  if (isInternalNote) {
    return {
      ...basedStyles,
      border: `1px solid ${alpha(theme.palette.custom.OV.extended.Signature.colorContainer, 0.75)}`,
      ...privateNoteButtonStyles(theme),
    };
  }

  return {
    ...basedStyles,
    border: `1px solid ${alpha(theme.palette.custom.OV.sys.outlineVariant, 0.75)}`,
  };
});

const MessageTypeSelectorBackground = styled(Stack, {
  name: 'MessageTypeSelectorBackground',
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile, theme }) => ({
  ...(isMobile && { minHeight: 0, maxHeight: '50%' }),
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
  alignItems: 'stretch',
  flex: '0 0 min-content',
}));

interface onSubmitInput {
  message: {
    body: string;
    isPrivate: boolean;
    mentions?: EntityIdentifier[];
    conversationMessageId?: string;
    smsSessionId?: string;
  };
  macros: AppliedMacroAction[];
  activeChannels?: ChannelIdentifiers[];
  participants: SubmitConversationDataParticipant[];
  sendAndActions?: DraftScheduleSendAndAction[];
}

interface InputFieldProps {
  onSubmit: (input: onSubmitInput) => Promise<void>;
  messageTypeOptions: MessageTypeSelectorProps['options'];
  conversation: Conversation;
  participants: ConversationParticipantItem[];
  files?: RichTextEditorFile[];
  setFiles?: Dispatch<SetStateAction<RichTextEditorFile[]>>;
  selectedParticipantsState: UseSelectedParticipants;
  fieldAutoSize?: boolean;
  fieldMinHeight?: string;
  /** Folds the composer into the strip; when absent the X and Escape do nothing. */
  onCollapse?: () => void;
  /** While folded the strip binds the macro and snooze keys, so the hidden card lets go. */
  isFolded?: boolean;
}

export interface InputFieldHandle {
  switchMessageType: (messageType: ConversationChannelTypeExtended) => void;
  focusEditor: () => void;
  /** Opens the macros modal; `onApplied` fires once a macro landed in the editor. */
  openMacros: (onApplied?: () => void) => void;
  openSnooze: () => void;
}

const editorOptions = { clearOnSubmit: true };

const COLLAPSE_ICON_SIZE = 15;

const collapseButtonSx: SxProps<Theme> = (theme) => ({
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '0.375rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '&:hover': {
    backgroundColor: alpha(theme.palette.custom.OV.sys.onBackground, 0.08),
    color: theme.palette.custom.OV.sys.onBackground,
  },
});

const CHAT_PLACEHOLDER_TEXT = `Write, type '/' for commands, '@' for mentions…`;

// Glass mode: the AI-suggestion header and holiday banner share this box with the card
// (InputFieldBackground) — a flex column, so the banners keep their natural height and the
// card flexGrows into whatever slack is left above its own floor.
const inputFieldRootSx: SxProps<Theme> = {
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  // The default min-height:auto refuses to shrink below content, which let the editor's
  // autogrow push this box past the dock's cap and clipped the tools row out of sight.
  minHeight: 0,
};

const placeholderOptions = {
  placeholder: CHAT_PLACEHOLDER_TEXT,
  emptyLinePlaceholderText: CHAT_PLACEHOLDER_TEXT,
};

export const InputField = forwardRef<InputFieldHandle, InputFieldProps>(function InputField(
  {
    onSubmit,
    messageTypeOptions,
    conversation,
    participants,
    files,
    setFiles,
    selectedParticipantsState,
    fieldAutoSize = true,
    fieldMinHeight,
    onCollapse,
    isFolded = false,
  },
  ref,
) {
  const { displayTime } = useDisplayTime();
  const { uploadFile } = useFileUpload();
  const { hasAnyBanner } = useComposerBanners();

  const buildAttachmentsManifest = useCallback(
    async (sourceFiles: RichTextEditorFile[]): Promise<DraftAttachmentInput[]> => {
      const newFiles = sourceFiles.filter(isFile);
      const existing = sourceFiles.filter(isConversationAttachmentWithUrl);

      const uploaded = newFiles.length
        ? await getAttachmentsFromEditor({
            files: newFiles,
            attachmentsBucketFolder: `${conversation.partnerId}/${conversation.conversationId}`,
            uploadFile,
          })
        : [];

      return [
        ...uploaded.map((file) => ({
          fileKey: file.url.split('/').at(-1)?.split('.')[0] ?? '',
          fileName: file.name,
          contentType: file.type,
          size: file.size,
        })),
        ...existing.map((attachment) => ({
          fileKey: attachment.fileKey,
          fileName: attachment.fileName,
          contentType: attachment.contentType,
          size: attachment.size,
        })),
      ];
    },
    [conversation.conversationId, conversation.partnerId, uploadFile],
  );

  const {
    getToParticipant,
    removeParticipant,
    selectedParticipants,
    setToParticipant,
    toIsModified,
    ...selectedParticipantsCallbacks
  } = selectedParticipantsState;
  const [isOffline, setIsOffline] = useState(false);

  const location = useLocation();
  const forceMessageType = (
    location.state as { forceMessageType?: ConversationChannelTypeExtended }
  )?.forceMessageType;

  const { isMobile } = useScreenDimensions();
  const editorRef = useRef<Editor | null>(null);
  const lastAppliedRef = useRef(''); // what we last pushed into the editor
  const hydratedRawDraftRef = useRef<string | null>(null); // the draft that push came from
  const appliedEditorDocRef = useRef<string | null>(null); // the editor doc that push produced
  const rteSubmitButtonRef = useRef<MutableRefObject<HTMLButtonElement | null> | null>(null);
  const sendNowHandlerRef = useRef<(() => void) | null>(null);
  const pendingSendActionsRef = useRef<DraftScheduleSendAndAction[] | null>(null);
  const pendingSendDeferredRef = useRef<{
    resolve: () => void;
    reject: (error: unknown) => void;
  } | null>(null);

  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { checkValidity } = useGroupRules();
  const canSendPublicReplies = checkValidity([
    UserGroupRules.AccessToSendExternalConversationMessages,
  ]);

  const showSmsSwitch = useSmsSessionContext((v) => v.showSmsSwitch);
  const hasOpenSession = useSmsSessionContext((v) => v.hasOpenSession);
  const openSessionChannelId = useSmsSessionContext((v) => v.openSessionChannelId);
  const smsDisabledReason = useSmsSessionContext((v) => v.smsDisabledReason);
  const startSmsSession = useSmsSessionContext((v) => v.startSession);
  const openSessionId = useSmsSessionContext((v) => v.openSessionId);

  const conversationParticipations = useChatCommonData((s) => s.conversationParticipations);
  const smsSessionParticipation = useMemo(
    () => findSmsSessionParticipation(conversationParticipations, openSessionChannelId),
    [conversationParticipations, openSessionChannelId],
  );

  // SMS stays selectable without an open session — sending starts one. The mode
  // follows the session: SMS while one is open, email otherwise; it re-derives when the
  // conversation changes, the gates revoke the switch, or the session opens / ends.
  const [smsMode, setSmsMode] = useState<SmsChannelMode>('email');
  useEffect(() => {
    setSmsMode(showSmsSwitch && hasOpenSession ? 'sms' : 'email');
  }, [conversation.conversationId, showSmsSwitch, hasOpenSession]);

  const { drafts: userDrafts } = useConversationMessageDraft();
  const restoredReplyIsPrivate = useMemo(() => {
    const latest = userDrafts.find((draft) => draft.conversation?.id === conversation.id);
    return latest ? latest.isPrivate : null;
  }, [userDrafts, conversation.id]);

  const userChangedModeRef = useRef(false);
  const replyModeRestoredRef = useRef(false);

  const [isInternalNoteChosen, setIsInternalNoteChosen] = useState(() => {
    if (forceMessageType) {
      return forceMessageType === ConversationChannelTypeExtended.InternalNote;
    }
    if (restoredReplyIsPrivate !== null) {
      return restoredReplyIsPrivate;
    }
    return true;
  });
  // without public-reply access the composer stays a note, whatever a draft or switch asked for
  const isInternalNote = isInternalNoteChosen || !canSendPublicReplies;

  useEffect(() => {
    if (forceMessageType) return;
    if (userChangedModeRef.current || replyModeRestoredRef.current) return;
    if (restoredReplyIsPrivate === null) return;
    replyModeRestoredRef.current = true;
    setIsInternalNoteChosen(restoredReplyIsPrivate);
  }, [forceMessageType, restoredReplyIsPrivate]);

  // Base draft instance
  const {
    draftContent: baseDraftContent,
    debounceUpdateDraft: debounceUpdateBaseDraft,
    flushUpdateDraft: flushUpdateBaseDraft,
    clearDraft: clearBaseDraft,
    draftSchedule: baseDraftSchedule,
    updateDraftSchedule: updateBaseDraftSchedule,
  } = useConversationDraft({
    conversationId: conversation.id,
    conversationPartnerId: conversation.partnerId,
    isPrivate: isInternalNote,
    conversation,
  });

  // Scheduled draft instance (separate)
  const {
    draftContent: scheduledDraftContent,
    debounceUpdateDraft: debounceUpdateScheduledDraft,
    flushUpdateDraft: flushUpdateScheduledDraft,
    clearDraft: clearScheduledDraft,
    draftSchedule: scheduledDraftSchedule,
    updateDraftSchedule: updateScheduledDraftSchedule,
    isDraftSaving: isScheduledDraftSaving,
    serverDraft: scheduledServerDraft,
  } = useConversationDraft({
    conversationId: conversation.id,
    conversationPartnerId: conversation.partnerId,
    isPrivate: isInternalNote,
    conversation,
    scheduled: true,
  });

  const scheduledServerAttachments = useMemo(
    () =>
      (scheduledServerDraft?.attachments ?? []).filter(
        (attachment): attachment is NonNullable<typeof attachment> =>
          Boolean(attachment?.fileKey),
      ),
    [scheduledServerDraft?.attachments],
  );
  const scheduledAttachmentFiles = useMemo<RichTextEditorFile[]>(
    () =>
      scheduledServerAttachments.map(
        (attachment) =>
          ({
            ...attachment,
            url: getConversationAttachmentUrl(attachment) ?? '',
          }) as RichTextEditorFile,
      ),
    [scheduledServerAttachments],
  );
  const [scheduledFiles, setScheduledFiles] = useState<RichTextEditorFile[]>([]);

  useEffect(() => {
    setScheduledFiles(scheduledAttachmentFiles);
  }, [scheduledAttachmentFiles]);

  const apolloClient = useApolloClient();
  const [deleteScheduledDraftMutation] = useMutation<DeleteConversationMessageDraft>(
    DELETE_CONVERSATION_MESSAGE_DRAFT,
  );

  const deleteScheduledDraftRecord = useCallback(
    async (draft: { id: string; conversationId: string; isPrivate: boolean }) => {
      const removeFromCache = (targetId: string) => {
        const { cache } = apolloClient;
        const cacheId = cache.identify({
          __typename: 'ConversationMessageDraft',
          id: targetId,
        });
        if (cacheId) {
          cache.evict({ id: cacheId });
        }
        cache.updateQuery<GetUserConversationMessageDraft>(
          { query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY },
          (existing) =>
            existing
              ? {
                  getUserConversationMessageDraft: {
                    records: existing.getUserConversationMessageDraft.records.filter(
                      (record) => record.id !== targetId,
                    ),
                  },
                }
              : existing,
        );
        cache.gc();
      };

      let id = draft.id;

      if (isOptimisticDraftId(id)) {
        const realId = await findServerDraftIdByScope(apolloClient, {
          conversationId: draft.conversationId,
          isPrivate: draft.isPrivate,
          isScheduled: true,
        });

        if (!realId) {
          removeFromCache(id);
          clearLocalDraftScope(
            draft.conversationId,
            draft.isPrivate,
            ConversationDraftScope.Scheduled,
          );
          return;
        }
        id = realId;
      }

      await deleteScheduledDraftMutation({
        variables: {
          conditions: [{ attribute: 'id', operator: '=', value: id }],
        },
        optimisticResponse: {
          deleteConversationMessageDraft: [{ id }],
        },
        update: () => removeFromCache(id),
        context: { omitIsolationModeHeader: true },
      });
      clearLocalDraftScope(
        draft.conversationId,
        draft.isPrivate,
        ConversationDraftScope.Scheduled,
      );
    },
    [apolloClient, deleteScheduledDraftMutation],
  );

  const handleSendAndActions = useCallback(
    async (actions: DraftScheduleSendAndAction[]): Promise<void> => {
      if (!actions.length) return;
      if (!sendNowHandlerRef.current) {
        showErrorAlert('Unable to send message right now. Please try again.');
        return;
      }

      pendingSendActionsRef.current = actions;
      const promise = new Promise<void>((resolve, reject) => {
        pendingSendDeferredRef.current = { resolve, reject };
      });

      try {
        sendNowHandlerRef.current();
      } catch (error) {
        pendingSendActionsRef.current = null;
        pendingSendDeferredRef.current = null;
        throw error;
      }

      await promise;
    },
    [showErrorAlert],
  );

  const [macroActions, setMacroActions] = useState<AppliedMacroAction[]>([]);
  const macroActionsRef = useRef(macroActions);
  const pendingSeedRef = useRef<string | null>(null);
  const aiMacroContextRef = useRef<AiMacroDraftContext | null>(null);
  useEffect(() => {
    macroActionsRef.current = macroActions;
  }, [macroActions]);
  useEffect(() => {
    pendingSeedRef.current = null;
    aiMacroContextRef.current = null;
  }, [conversation.id]);

  const syncScheduledDraftRef =
    useRef<(schedule?: DraftSchedule) => Promise<void> | void>(undefined);

  const syncScheduledDraft = useCallback(async (schedule?: DraftSchedule): Promise<void> => {
    const handler = syncScheduledDraftRef.current;
    if (handler) {
      await handler(schedule);
    }
  }, []);

  const flushActiveDraftRef =
    useRef<(options?: { persistUneditedSuggestion?: boolean }) => void | Promise<void>>(
      undefined,
    );

  // Send Later persists whatever is in the composer, suggestion included
  const flushActiveDraft = useCallback(
    () => flushActiveDraftRef.current?.({ persistUneditedSuggestion: true }),
    [],
  );

  const scheduledDraftContentRef = useRef(scheduledDraftContent);

  useEffect(() => {
    scheduledDraftContentRef.current = scheduledDraftContent;
  }, [scheduledDraftContent]);

  const isViewingScheduledMessageRef = useRef(false);
  const baseDraftContentRef = useRef(baseDraftContent);

  useEffect(() => {
    baseDraftContentRef.current = baseDraftContent;
  }, [baseDraftContent]);

  const convertScheduledDraftToRegular = useCallback(async () => {
    const flushActive = flushActiveDraftRef.current;
    if (flushActive) {
      await flushActive({ persistUneditedSuggestion: true });
    }

    const storedScheduledContent = scheduledDraftContentRef.current || '';
    const parsedScheduledDoc = parseJsonSafely<EditorDoc>(storedScheduledContent);
    const snapshot = isViewingScheduledMessageRef.current
      ? buildDraftJson(editorRef.current, macroActionsRef.current, parsedScheduledDoc)
      : storedScheduledContent;

    scheduledDraftContentRef.current = snapshot;
    flushUpdateBaseDraft(snapshot);
    await updateBaseDraftSchedule({
      scheduledAt: null,
      sideEffects: null,
      sendUnless: null,
      discardedAt: null,
    });
    if (scheduledServerDraft?.id) {
      await deleteScheduledDraftRecord({
        id: scheduledServerDraft.id,
        conversationId: scheduledServerDraft.conversationId ?? conversation.id,
        isPrivate: Boolean(scheduledServerDraft.isPrivate),
      });
    } else {
      clearLocalDraftScope(
        conversation.id,
        scheduledServerDraft?.isPrivate ?? isInternalNote,
        ConversationDraftScope.Scheduled,
      );
    }
    scheduledDraftContentRef.current = '';
    lastAppliedRef.current = snapshot;
  }, [
    conversation.id,
    deleteScheduledDraftRecord,
    flushUpdateBaseDraft,
    isInternalNote,
    scheduledServerDraft,
    updateBaseDraftSchedule,
  ]);

  const { confirm } = useConfirmationModal();

  // Unschedule while keeping the current input (do not override base content)
  const unscheduleDraftKeepCurrent = useCallback(async () => {
    const flushActive = flushActiveDraftRef.current;
    if (flushActive) {
      await flushActive();
    }

    // Clear any schedule on the base draft
    await updateBaseDraftSchedule({
      scheduledAt: null,
      sideEffects: null,
      sendUnless: null,
      discardedAt: null,
    });

    // Remove the scheduled draft record/scope
    if (scheduledServerDraft?.id) {
      await deleteScheduledDraftRecord({
        id: scheduledServerDraft.id,
        conversationId: scheduledServerDraft.conversationId ?? conversation.id,
        isPrivate: Boolean(scheduledServerDraft.isPrivate),
      });
    } else {
      clearLocalDraftScope(
        conversation.id,
        scheduledServerDraft?.isPrivate ?? isInternalNote,
        ConversationDraftScope.Scheduled,
      );
    }

    scheduledDraftContentRef.current = '';
    // Keep editor content as-is; do not change lastAppliedRef
  }, [
    conversation.id,
    deleteScheduledDraftRecord,
    isInternalNote,
    scheduledServerDraft,
    updateBaseDraftSchedule,
  ]);

  const computeIsMessageEmpty = useCallback((): boolean => {
    const hasText = editorHasContent(editorRef.current);
    const hasFiles = (files?.length ?? 0) > 0;
    return !(hasText || hasFiles);
  }, [files?.length]);

  // Wrapper that asks whether to keep current input or scheduled draft content
  const unscheduleDraftWithConfirm = useCallback(async () => {
    // If currently viewing scheduled message, check base draft content instead
    const messageIsEmpty = isViewingScheduledMessageRef.current
      ? !(baseDraftContentRef.current && baseDraftContentRef.current.trim().length > 0)
      : computeIsMessageEmpty();
    if (messageIsEmpty) {
      await convertScheduledDraftToRegular();
      return;
    }

    const confirmed = await confirm({
      title: 'Unschedule message?',
      body: 'The scheduled message is discarded, and the text you typed stays in the composer.',
      confirmLabel: 'Unschedule message',
      cancelLabel: 'Keep schedule',
      danger: true,
    });

    if (confirmed) {
      await unscheduleDraftKeepCurrent();
    }
  }, [
    computeIsMessageEmpty,
    confirm,
    convertScheduledDraftToRegular,
    unscheduleDraftKeepCurrent,
  ]);

  const {
    hasScheduledMessage,
    scheduledLabel,
    scheduledDiscardedLabel,
    isScheduleDiscarded,
    sendOptions,
    isScheduleModalOpen,
    openScheduleModal,
    closeScheduleModal,
    handleSendOptionSelect,
    handleScheduleModalSubmit,
    handleDiscardScheduledMessage,
    handleViewScheduledMessage,
    handleDoneViewingScheduledMessage,
    handleReschedule,
    isViewingScheduledMessage,
    isApplyingSendOption,
    scheduledConditions,
  } = useDraftScheduling({
    scheduledDraftSchedule,
    displayTime,
    // @ts-expect-error temp
    flushDraft: flushActiveDraft,
    updateDraftSchedule: updateScheduledDraftSchedule,
    executeSendAndActions: handleSendAndActions,
    syncScheduledDraft,
    unscheduleDraft: unscheduleDraftWithConfirm,
  });

  const [isMessageEmpty, setIsMessageEmpty] = useState(true);

  useEffect(() => {
    setIsMessageEmpty(computeIsMessageEmpty());

    const id = window.setInterval(() => {
      setIsMessageEmpty(computeIsMessageEmpty());
    }, 250);

    return () => window.clearInterval(id);
  }, [computeIsMessageEmpty]);

  const {
    suggestedReply,
    bannerState: suggestedReplyBannerState,
    isUneditedSuggestionInEditor,
    canAutoHydrate: canAutoHydrateSuggestedReply,
    hydrateIfEmpty: hydrateSuggestedReply,
    view: viewSuggestedReply,
    dismiss: dismissSuggestedReply,
    restorePersonalDraft: restorePersonalDraftBehindSuggestion,
    reset: resetSuggestedReply,
    onSent: onSuggestedReplySent,
  } = useComposerSuggestedReply({
    conversationId: conversation.id,
    isPrivate: isInternalNote,
    editorRef,
    lastAppliedRef,
    personalDraftContent: baseDraftContent,
    isViewingScheduledMessage,
    editorIsEmpty: isMessageEmpty,
  });

  // the hydration effect reads these through the ref so only `activeRawDraft` can re-run it
  const suggestedReplyApiRef = useRef({
    isUneditedSuggestionInEditor,
    canAutoHydrateSuggestedReply,
    hydrateSuggestedReply,
  });
  suggestedReplyApiRef.current = {
    isUneditedSuggestionInEditor,
    canAutoHydrateSuggestedReply,
    hydrateSuggestedReply,
  };

  const handleDismissSuggestedReply = useCallback(() => {
    if (dismissSuggestedReply()) {
      appliedEditorDocRef.current = readEditorDocJson(editorRef.current);
    }
  }, [dismissSuggestedReply]);

  useRegisterQuickActions(
    useMemo(
      () => [
        {
          id: 'conversation-open-send-options',
          title: 'Open send options',
          description: 'Schedule or apply send-and actions',
          icon: <DynamicIcon name="calendar-clock" library="lucide" />,
          group: 'Conversation Actions',
          handler: openScheduleModal,
          order: 180,
          keywords: ['schedule', 'send', 'snooze'],
        },
      ],
      [openScheduleModal],
    ),
  );

  useEffect(() => {
    isViewingScheduledMessageRef.current = isViewingScheduledMessage;
  }, [isViewingScheduledMessage]);

  const activeRawDraft = useMemo(
    () => (isViewingScheduledMessage ? scheduledDraftContent : baseDraftContent) || '',
    [isViewingScheduledMessage, scheduledDraftContent, baseDraftContent],
  );
  const aiGenerationScope = `${conversation.id}:${isInternalNote ? 'note' : 'reply'}:${
    isViewingScheduledMessage ? 'scheduled' : 'base'
  }`;

  useEffect(() => {
    if (!isAiMacroContextForScope(aiMacroContextRef.current, aiGenerationScope)) {
      aiMacroContextRef.current = null;
    }
  }, [aiGenerationScope]);

  const hasSendableScheduledMessage =
    hasScheduledMessage && isDraftNonEmpty(scheduledDraftContent);

  const parsedDraft = useMemo(
    () => parseJsonSafely<EditorDoc>(activeRawDraft),
    [activeRawDraft],
  );

  useEffect(() => {
    syncScheduledDraftRef.current = async (schedule?: DraftSchedule) => {
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      const attachments = await buildAttachmentsManifest(files ?? []);
      flushUpdateScheduledDraft(snapshot, schedule, attachments);

      await clearBaseDraft();

      editorRef.current?.commands?.clearContent?.(true);
      lastAppliedRef.current = '';
      macroActionsRef.current = [];
      pendingSeedRef.current = null;
      aiMacroContextRef.current = null;
      setMacroActions([]);
      setFiles?.([]);
      resetSuggestedReply();
    };
  }, [
    buildAttachmentsManifest,
    files,
    clearBaseDraft,
    flushUpdateScheduledDraft,
    macroActions,
    parsedDraft,
    resetSuggestedReply,
    setFiles,
    setMacroActions,
  ]);

  useEffect(() => {
    flushActiveDraftRef.current = (options) => {
      if (!options?.persistUneditedSuggestion && isUneditedSuggestionInEditor()) return;
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      lastAppliedRef.current = snapshot;
      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
        return;
      }
      flushUpdateBaseDraft(snapshot);
    };
  }, [
    flushUpdateBaseDraft,
    flushUpdateScheduledDraft,
    isUneditedSuggestionInEditor,
    isViewingScheduledMessage,
    macroActions,
    parsedDraft,
  ]);

  const notifySendAndActions = useCallback(
    (actions: DraftScheduleSendAndAction[]): void => {
      if (!actions.length) return;

      const fragments: string[] = [];

      actions.forEach((action) => {
        if (action.type === ConversationDraftSideEffectActionType.Status) {
          const resolved = resolveStatusAction(action.value);
          if (resolved) {
            fragments.push(resolved.label);
          }
        }

        if (action.type === ConversationDraftSideEffectActionType.Snooze && action.value) {
          const snoozeAt = dayjs(action.value);
          if (snoozeAt.isValid()) {
            const datePart = snoozeAt.format('MMM D');
            fragments.push(
              `conversation snoozed until ${datePart} at ${displayTime(snoozeAt)}`,
            );
          } else {
            fragments.push('conversation snoozed');
          }
        }
      });

      if (!fragments.length) {
        return;
      }

      const message = fragments.join(' and ');
      showSuccessAlert(`Message sent and ${message}`);
    },
    [displayTime, showSuccessAlert],
  );

  useEffect(() => {
    const aiMacroContext = aiMacroContextRef.current;
    const draftActions = parsedDraft ? readMacrosFromDoc(parsedDraft) : [];
    setMacroActions(resolveAiMacroActions(aiMacroContext, aiGenerationScope, draftActions));
  }, [aiGenerationScope, parsedDraft]);

  // Hydrate editor from active draft, or from the suggested reply when there is no draft
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const suggestedReplyApi = suggestedReplyApiRef.current;
    const decision = decideEditorContent({
      activeRawDraft,
      previousRawDraft: hydratedRawDraftRef.current,
      editorHasContent: editorHasContent(editor),
      editorHoldsAppliedContent: readEditorDocJson(editor) === appliedEditorDocRef.current,
      uneditedSuggestionInEditor: suggestedReplyApi.isUneditedSuggestionInEditor(),
      suggestionAvailable: suggestedReplyApi.canAutoHydrateSuggestedReply(),
    });

    if (decision === 'keep') return;

    if (decision === 'apply-draft') {
      hydratedRawDraftRef.current = activeRawDraft;
      applyEditorContent(editor, activeRawDraft, lastAppliedRef);
    } else {
      hydratedRawDraftRef.current = '';
      editor.commands?.clearContent?.(true);
      lastAppliedRef.current = '';
      if (decision === 'hydrate-suggestion') suggestedReplyApi.hydrateSuggestedReply();
    }

    appliedEditorDocRef.current = readEditorDocJson(editor);
  }, [activeRawDraft]);

  // Persist json on blur to the active draft
  const handleEditorBlur = useCallback(
    (_: React.FocusEvent, editor: Editor | null) => {
      if (!editor) return;
      if (isUneditedSuggestionInEditor()) return;
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      if (snapshot === lastAppliedRef.current) return;
      lastAppliedRef.current = snapshot;

      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
      } else {
        flushUpdateBaseDraft(snapshot);
      }

      appliedEditorDocRef.current = readEditorDocJson(editor);

      // the agent just saved an empty composer: the suggestion may not take it back on its own
      if (!editorHasContent(editor)) resetSuggestedReply();
    },
    [
      isUneditedSuggestionInEditor,
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      flushUpdateBaseDraft,
      flushUpdateScheduledDraft,
      resetSuggestedReply,
    ],
  );

  // persist attachment add/remove made while viewing the scheduled message.
  useEffect(() => {
    if (!isViewingScheduledMessage) return undefined;

    const attachmentsKey = (list: RichTextEditorFile[]): string =>
      list
        .map((file) => (isFile(file) ? file.name : file.fileKey))
        .sort()
        .join('|');

    if (attachmentsKey(scheduledFiles) === attachmentsKey(scheduledAttachmentFiles)) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
      void buildAttachmentsManifest(scheduledFiles).then((attachments) =>
        flushUpdateScheduledDraft(snapshot, undefined, attachments),
      );
    }, 600);

    return () => clearTimeout(timer);
  }, [
    isViewingScheduledMessage,
    scheduledFiles,
    scheduledAttachmentFiles,
    buildAttachmentsManifest,
    flushUpdateScheduledDraft,
    macroActions,
    parsedDraft,
  ]);

  // AI suggestion writes to the active draft
  const setAiSuggestion = useCallback(
    (input: string) => {
      applyEditorContent(editorRef.current, input, lastAppliedRef);
      const aiMacroContext = aiMacroContextRef.current;
      const actions = resolveAiMacroActions(
        aiMacroContext,
        aiGenerationScope,
        macroActionsRef.current,
      );
      const snapshot = buildDraftJson(editorRef.current, actions, parsedDraft);
      lastAppliedRef.current = snapshot;
      macroActionsRef.current = actions;
      setMacroActions(actions);
      if (isViewingScheduledMessage) {
        flushUpdateScheduledDraft(snapshot);
      } else {
        flushUpdateBaseDraft(snapshot);
      }
    },
    [
      aiGenerationScope,
      isViewingScheduledMessage,
      parsedDraft,
      flushUpdateBaseDraft,
      flushUpdateScheduledDraft,
    ],
  );

  const getCurrentEditorContent = useCallback(() => editorRef.current?.getHTML() || '', []);

  const {
    pendingAiSuggestion,
    selectedPromptName,
    isGeneratingAiSuggestion,
    handleRefineSuggestion,
    handleAcceptAiSuggestion: acceptAiSuggestion,
    handleDismissAiSuggestion: dismissAiSuggestion,
    resetAiAssistant,
    getInitialSuggestion,
    conversationMessageId,
    aiGenerationError,
    retryGeneration,
    dismissAiGenerationError,
  } = useAiAssistant({
    setContent: editorRef.current ? setAiSuggestion : undefined,
    getCurrentEditorContent,
    conversationId: conversation.id,
    conversationPartnerId: conversation.partnerId,
    generationScope: aiGenerationScope,
  });

  const handleAcceptAiSuggestion = useCallback(() => {
    acceptAiSuggestion();
    aiMacroContextRef.current = null;
  }, [acceptAiSuggestion]);

  const handleDismissAiSuggestion = useCallback(() => {
    dismissAiSuggestion();
    aiMacroContextRef.current = null;
  }, [dismissAiSuggestion]);

  const { selectedConversationRequester, setPartnerSelectedConversationRequester } =
    useSelectedConversationRequesterStore((state) => state);
  const currentRequesterId = useRef(selectedConversationRequester?.id);

  useEffect(() => {
    if (currentRequesterId.current === selectedConversationRequester?.id) return;
    currentRequesterId.current = selectedConversationRequester?.id;

    if (!selectedParticipants.to && !selectedConversationRequester) return;

    if (!selectedConversationRequester) {
      if (!toIsModified) {
        removeParticipant(getToParticipant()!);
      }
      return;
    }

    if (
      !selectedConversationRequester.email ||
      selectedConversationRequester.participantEntity === ConversationParticipantEntity.Rsm
    ) {
      return;
    }

    if (
      !selectedParticipants.to ||
      !isSameParticipantOption(selectedParticipants.to, selectedConversationRequester)
    ) {
      setToParticipant(selectedConversationRequester);
    }
  }, [
    getToParticipant,
    toIsModified,
    removeParticipant,
    selectedConversationRequester,
    selectedParticipants.items,
    selectedParticipants.to,
    setToParticipant,
  ]);

  const needToChangeCurrentRequester = useMemo(() => {
    return (
      !isInternalNote &&
      toIsModified &&
      needToChangeRequester(selectedParticipants.to, selectedConversationRequester)
    );
  }, [isInternalNote, selectedConversationRequester, selectedParticipants.to, toIsModified]);

  const customActionChips = useMemo(() => {
    if (needToChangeCurrentRequester) {
      return [getSetRequesterChip(selectedParticipants.to!)];
    }
    return [];
  }, [needToChangeCurrentRequester, selectedParticipants.to]);

  const { user } = useCurrentUserProfile();
  const hasAccessToAiAssistant = checkValidity([UserGroupRules.AiAssistant]);

  const onMacroSelected = useCallback(
    (macro: Macro, interpolatedMessage: string) => {
      const usesAiPrompt = resolveMacroSnippetSource(macro) === MacroSnippetSource.AiPrompt;

      if (usesAiPrompt && !hasAccessToAiAssistant) {
        // Refuse the whole macro rather than attaching its actions and never
        // producing the message they were meant to accompany.
        showErrorAlert('This macro uses AI writing, which you do not have access to.');
        return;
      }

      if (macro.messagePrivacy === 'public' && !canSendPublicReplies) {
        showErrorAlert('This macro sends a public reply, which you do not have access to.');
        return;
      }

      const nextIsNote =
        macro.messagePrivacy === 'private' ||
        (macro.messagePrivacy !== 'public' && isInternalNote);

      // validate + merge actions (chips)
      const updated = validateMacroActions([
        ...macroActionsRef.current,
        ...macro.actions.map((a) => ({ ...a, id: crypto.randomUUID(), macroId: macro.id })),
      ] as MacroActionRule[]);
      const editor = editorRef.current;
      const aiGenerationRequest: AiGenerationRequest | null =
        usesAiPrompt && user?.userId
          ? {
              promptId: macro.aiPromptId ?? undefined,
              customPrompt: macro.aiPromptText ?? undefined,
              // The bar names the macro, not the prompt — that is what the agent
              // picked, and what they need to recognise when undoing.
              promptName: macro.name,
              userId: user.userId,
              userInput: editor?.getHTML() || '',
            }
          : null;
      const didSwitchComposer = nextIsNote !== isInternalNote;
      const targetGenerationScope = `${conversation.id}:${nextIsNote ? 'note' : 'reply'}:${
        isViewingScheduledMessage ? 'scheduled' : 'base'
      }`;

      // A new macro replaces any in-flight generation immediately, including
      // when this macro changes composer mode before its seed effect can run.
      resetAiAssistant();
      aiMacroContextRef.current = aiGenerationRequest
        ? {
            actions: updated,
            generationScope: targetGenerationScope,
            request: aiGenerationRequest,
          }
        : null;
      macroActionsRef.current = updated;

      // The text goes in before the snapshot: the snapshot is what seeds the draft, and when the
      // macro switches composer mode that draft is the only thing the new mode hydrates from.
      const snapshot = applyMacroContent(editor, interpolatedMessage, {
        macros: updated,
        prev: parsedDraft,
      });

      lastAppliedRef.current = snapshot;

      // seed into the correct draft
      if (didSwitchComposer) {
        userChangedModeRef.current = true;
        pendingSeedRef.current = snapshot;
        setIsInternalNoteChosen(nextIsNote);
      } else {
        if (isViewingScheduledMessage) {
          debounceUpdateScheduledDraft(snapshot);
        } else {
          debounceUpdateBaseDraft(snapshot);
        }
      }

      setMacroActions(updated);
      showSuccessAlert('Macro applied');

      if (aiGenerationRequest && !didSwitchComposer) {
        // The actions above are already attached and the editor still holds
        // whatever the agent typed, so a failed generation costs them nothing.
        void getInitialSuggestion(aiGenerationRequest);
      }
    },
    [
      isInternalNote,
      isViewingScheduledMessage,
      conversation.id,
      parsedDraft,
      debounceUpdateBaseDraft,
      debounceUpdateScheduledDraft,
      showSuccessAlert,
      showErrorAlert,
      hasAccessToAiAssistant,
      canSendPublicReplies,
      getInitialSuggestion,
      resetAiAssistant,
      user?.userId,
    ],
  );

  // after scope flips (because of a macro), seed new scope once via the hook
  useEffect(() => {
    const pendingSeed = pendingSeedRef.current;
    if (!pendingSeed) return;

    const aiMacroContext = aiMacroContextRef.current;
    const shouldStartAiGeneration = isAiMacroContextForScope(
      aiMacroContext,
      aiGenerationScope,
    );

    if (isViewingScheduledMessage) {
      if (shouldStartAiGeneration) flushUpdateScheduledDraft(pendingSeed);
      else debounceUpdateScheduledDraft(pendingSeed);
    } else {
      if (shouldStartAiGeneration) flushUpdateBaseDraft(pendingSeed);
      else debounceUpdateBaseDraft(pendingSeed);
    }
    pendingSeedRef.current = null;

    if (shouldStartAiGeneration) {
      void getInitialSuggestion(aiMacroContext.request);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternalNote]);

  // delete exactly one chip (pure json meta update)
  const onActionDelete = useCallback(
    (index: number) => {
      const next = macroActions.filter((_, i) => i !== index);
      const snapshot = buildDraftJson(editorRef.current, next, parsedDraft);
      lastAppliedRef.current = snapshot;
      macroActionsRef.current = next;
      const aiMacroContext = aiMacroContextRef.current;
      if (isAiMacroContextForScope(aiMacroContext, aiGenerationScope)) {
        aiMacroContextRef.current = { ...aiMacroContext, actions: next };
      }
      if (isViewingScheduledMessage) {
        debounceUpdateScheduledDraft(snapshot);
      } else {
        debounceUpdateBaseDraft(snapshot);
      }
      setMacroActions(next);
    },
    [
      isViewingScheduledMessage,
      aiGenerationScope,
      macroActions,
      parsedDraft,
      debounceUpdateBaseDraft,
      debounceUpdateScheduledDraft,
    ],
  );

  const handleResetMacroActions = useCallback(() => {
    const editor = editorRef.current;
    const hasMacros = macroActions.length > 0;
    const hasContent = editorHasContent(editor);

    if (!hasMacros && !hasContent) return;

    editor?.commands?.clearContent?.(true);
    lastAppliedRef.current = '';
    macroActionsRef.current = [];
    pendingSeedRef.current = null;
    aiMacroContextRef.current = null;
    setMacroActions([]);
    resetAiAssistant();
    resetSuggestedReply();

    if (isViewingScheduledMessage) {
      void clearScheduledDraft();
    } else {
      void clearBaseDraft();
    }
  }, [
    clearBaseDraft,
    clearScheduledDraft,
    isViewingScheduledMessage,
    macroActions.length,
    resetAiAssistant,
    resetSuggestedReply,
  ]);

  const canDeleteMacroActions = checkValidity([UserGroupRules.AbilityToRemoveMacroActions]);

  const onSendMessage = useCallback(
    async (body: string, mentions?: EntityIdentifier[]) => {
      const deferred = pendingSendDeferredRef.current;
      const actions = pendingSendActionsRef.current;
      pendingSendActionsRef.current = null;

      const resolveDeferred = (): void => {
        deferred?.resolve?.();
        if (pendingSendDeferredRef.current === deferred) {
          pendingSendDeferredRef.current = null;
        }
      };

      const rejectDeferred = (error: unknown): void => {
        deferred?.reject?.(error);
        if (pendingSendDeferredRef.current === deferred) {
          pendingSendDeferredRef.current = null;
        }
      };

      try {
        const normalizedBody = normalizeOutgoingMessageHtml(body);

        if (!user || (!normalizedBody?.trim() && !files?.length)) {
          resolveDeferred();
          return;
        }

        const isSmsReply = !isInternalNote && smsMode === 'sms' && showSmsSwitch;

        if (!isInternalNote && !isSmsReply && !hasEmailChannel(getToParticipant())) {
          showErrorAlert(
            "A public reply can't be sent because the requester has no email address. Add an email to their client record, then try again.",
          );
          setTimeout(() =>
            applyEditorContent(editorRef.current, body || activeRawDraft, lastAppliedRef),
          );
          resolveDeferred();
          return;
        }

        // An SMS send must never fall back to email dispatch. The recipient is resolved BEFORE
        // any side effect: an open session is bound to one channel, so text that participation
        // only; otherwise the selected To must own an SMS channel of this conversation.
        let smsRecipient: SubmitConversationDataParticipant | null = null;
        if (isSmsReply) {
          const abortSmsSend = (message: ReactNode, options?: { autoHide: null }): void => {
            showErrorAlert(message, options);
            setTimeout(() =>
              applyEditorContent(editorRef.current, body || activeRawDraft, lastAppliedRef),
            );
            resolveDeferred();
          };

          const recipientParticipation = hasOpenSession
            ? smsSessionParticipation
            : findSmsParticipationForRecipient(
                conversationParticipations,
                participants,
                selectedParticipants.to,
              );

          if (!recipientParticipation) {
            if (hasOpenSession || findTwilioSmsParticipation(conversationParticipations)) {
              abortSmsSend(
                'Unable to send SMS: the selected recipient has no active SMS channel in this conversation.',
              );
              return;
            }
            // No SMS channel exists yet: the backend creates one for the conversation requester.
            if (needToChangeCurrentRequester) {
              abortSmsSend(
                "SMS sessions start with the conversation requester's phone. Select the requester as the recipient first.",
              );
              return;
            }
          }

          smsRecipient = recipientParticipation
            ? buildSmsParticipant(recipientParticipation, participants)
            : null;

          // Start-on-send: open the session on the resolved channel BEFORE submitting. The
          // response carries the freshly created channel when the participations cache can't yet.
          if (!hasOpenSession) {
            let startedSmsParticipation: StartedSmsParticipation | null = null;
            try {
              startedSmsParticipation = await startSmsSession(
                recipientParticipation?.conversationChannelId,
              );
            } catch (startError) {
              const openElsewhereId = parseSmsSessionAlreadyOpenError(startError);
              if (openElsewhereId) {
                // Sticky: the alert carries the link to the owning conversation.
                abortSmsSend(
                  <SmsSessionAlreadyOpenAlert friendlyConversationId={openElsewhereId} />,
                  { autoHide: null },
                );
                return;
              }
              abortSmsSend(
                startError instanceof Error
                  ? startError.message
                  : 'Unable to start SMS session. Please try again.',
              );
              return;
            }
            if (!smsRecipient) {
              smsRecipient = buildSmsParticipantFromStartResponse(
                startedSmsParticipation,
                selectedParticipants.to,
              );
            }
            if (!smsRecipient) {
              abortSmsSend(
                'Unable to send SMS: no active SMS channel found. Please refresh and try again.',
              );
              return;
            }
          }
        }

        if (!conversation.subject || conversation.subject === NO_SUBJECT_PLACEHOLDER) {
          showErrorAlert('Conversation subject must not be empty');
          setTimeout(() =>
            applyEditorContent(editorRef.current, body || activeRawDraft, lastAppliedRef),
          );
          resolveDeferred();
          return;
        }

        const senderParticipant = getSenderParticipant(
          user.userId,
          user.partnerId !== conversation.partnerId ? user.partnerId : undefined,
        );

        const preparedParticipants = smsRecipient
          ? [smsRecipient, senderParticipant]
          : getPreparedParticipants({
              selectedParticipants,
              conversation,
              userId: user?.userId,
              userPartnerId: user?.partnerId,
            });

        if (!isSmsReply && needToChangeCurrentRequester && selectedParticipants.to) {
          setPartnerSelectedConversationRequester(
            getRequesterPlaceholder(selectedParticipants.to),
          );
        }

        // this path is ONLY for "Send now" (not scheduling). For "Send later",
        // onSendMessage is NOT called — scheduling flow lives in useDraftScheduling.
        await onSubmit({
          message: {
            body: normalizedBody,
            isPrivate: isInternalNote,
            mentions,
            conversationMessageId,
            smsSessionId: isSmsReply ? (openSessionId ?? undefined) : undefined,
          },
          // The ref is updated synchronously whenever AI output rebuilds the
          // draft, so an immediate send cannot read stale persisted metadata.
          macros: resolveAiMacroActions(
            aiMacroContextRef.current,
            aiGenerationScope,
            macroActionsRef.current,
          ),
          participants: isInternalNote ? [senderParticipant] : preparedParticipants,
          activeChannels: isInternalNote ? undefined : getActiveChannels(preparedParticipants),
          sendAndActions: actions ?? undefined,
        });

        if (actions?.length) {
          notifySendAndActions(actions);
        }

        if (baseDraftSchedule?.sideEffects?.actions?.length) {
          await updateBaseDraftSchedule({ sideEffects: null }, { allowEmpty: true });
        }

        // Immediate send: clear the active draft instance and the editor UI.
        // (Scheduling does NOT come through here, so we do NOT touch scheduled drafts.)
        if (isViewingScheduledMessage) {
          await clearScheduledDraft();
        } else {
          await clearBaseDraft();
          onSuggestedReplySent();
        }

        // clear editor immediately to avoid lingering content while store updates
        editorRef.current?.commands?.clearContent?.(true);
        lastAppliedRef.current = '';
        macroActionsRef.current = [];
        pendingSeedRef.current = null;
        aiMacroContextRef.current = null;
        setMacroActions([]);
        resetAiAssistant();
        resolveDeferred();
      } catch (error) {
        rejectDeferred(error);
        throw error;
      }
    },
    [
      notifySendAndActions,
      baseDraftSchedule?.sideEffects?.actions?.length,
      activeRawDraft,
      user,
      files?.length,
      isInternalNote,
      getToParticipant,
      selectedParticipants,
      conversationParticipations,
      conversation,
      needToChangeCurrentRequester,
      onSubmit,
      conversationMessageId,
      resetAiAssistant,
      clearBaseDraft,
      clearScheduledDraft,
      showErrorAlert,
      setPartnerSelectedConversationRequester,
      aiGenerationScope,
      updateBaseDraftSchedule,
      isViewingScheduledMessage,
      smsMode,
      showSmsSwitch,
      smsSessionParticipation,
      participants,
      hasOpenSession,
      startSmsSession,
      openSessionId,
      onSuggestedReplySent,
    ],
  );

  const handleMessageTypeChange = useCallback(
    (val: ConversationChannelTypeExtended) => {
      const nextIsNote = val === ConversationChannelTypeExtended.InternalNote;

      if (!isUneditedSuggestionInEditor()) {
        const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
        lastAppliedRef.current = snapshot;

        if (isViewingScheduledMessage) {
          flushUpdateScheduledDraft(snapshot);
        } else {
          flushUpdateBaseDraft(snapshot);
        }
      }

      if (nextIsNote !== isInternalNote) {
        userChangedModeRef.current = true;
        setIsInternalNoteChosen(nextIsNote);
      }
    },
    [
      isUneditedSuggestionInEditor,
      isViewingScheduledMessage,
      macroActions,
      parsedDraft,
      flushUpdateBaseDraft,
      flushUpdateScheduledDraft,
      isInternalNote,
    ],
  );

  const { showModal: showSnoozeModal } = useModalMethods(Modals.SnoozeConversationModal);
  const { showModal: showMacrosModal } = useModalMethods(Modals.MacrosModal);
  useRegisterShortcuts(
    useMemo(
      () =>
        isFolded
          ? []
          : {
              ...appShortcuts.openSnooze,
              callback: () => {
                showSnoozeModal();
                return false;
              },
            },
      [isFolded, showSnoozeModal],
    ),
  );

  const openMacrosModal = useCallback(() => {
    showMacrosModal({
      conversation,
      onMacroSelected,
    });
  }, [conversation, onMacroSelected, showMacrosModal]);

  useImperativeHandle(
    ref,
    () => ({
      switchMessageType: handleMessageTypeChange,
      focusEditor: () => editorRef.current?.commands.focus('end'),
      openMacros: (onApplied) =>
        showMacrosModal({
          conversation,
          onMacroSelected: (macro, interpolatedMessage) => {
            onMacroSelected(macro, interpolatedMessage);
            onApplied?.();
          },
        }),
      openSnooze: () => showSnoozeModal(),
    }),
    [conversation, handleMessageTypeChange, onMacroSelected, showMacrosModal, showSnoozeModal],
  );

  const handleRootKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!onCollapse || event.key !== 'Escape') return;
      // Menus rendered in portals (mode, send options) still bubble through the React tree;
      // only Escape typed in the editor itself folds, and an open slash/mention menu closes first.
      if (!(event.target instanceof HTMLElement) || !event.target.closest('.ProseMirror')) {
        return;
      }
      if (hasOpenEditorSuggestion(editorRef.current)) return;
      editorRef.current?.commands.blur();
      onCollapse();
    },
    [onCollapse],
  );

  useRegisterQuickActions(
    useMemo(
      () => [
        {
          id: 'conversation-open-macros',
          title: 'Macros',
          description: 'Browse and apply macros',
          icon: <DynamicIcon name="zap" library="lucide" />,
          group: 'Conversation Actions',
          handler: openMacrosModal,
          order: 170,
          keywords: ['macros', 'templates', 'replies'],
        },
      ],
      [openMacrosModal],
    ),
  );

  const handleGetMentionPopup = useCallback(
    (
      mentionUpdatePopupStateSubject: Subject<Partial<MentionsPopupState>>,
      onMentionKeyDownRef: MutableRefObject<((event: KeyboardEvent) => boolean) | null>,
    ) => (
      <MentionPopup
        onMentionKeyDownRef={onMentionKeyDownRef}
        updatePopupStateSubject={mentionUpdatePopupStateSubject}
        partnerId={conversation.partnerId}
      />
    ),
    [conversation.partnerId],
  );

  // persist on hide/unload – write to the active draft
  const flushDraftNow = useCallback(() => {
    if (isUneditedSuggestionInEditor()) return;
    const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
    if (snapshot !== lastAppliedRef.current) {
      lastAppliedRef.current = snapshot;
    }
    if (isViewingScheduledMessage) {
      flushUpdateScheduledDraft(snapshot);
    } else {
      flushUpdateBaseDraft(snapshot);
    }
  }, [
    isUneditedSuggestionInEditor,
    isViewingScheduledMessage,
    macroActions,
    parsedDraft,
    flushUpdateBaseDraft,
    flushUpdateScheduledDraft,
  ]);

  const [isScheduleConflictOpen, setIsScheduleConflictOpen] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);

  // flush before toggling view to avoid losing edits
  const handleViewScheduledMessageSafe = useCallback(() => {
    if (isUneditedSuggestionInEditor()) {
      handleViewScheduledMessage();
      return;
    }
    const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
    lastAppliedRef.current = snapshot;
    flushUpdateBaseDraft(snapshot); // save base before switching
    handleViewScheduledMessage();
  }, [
    isUneditedSuggestionInEditor,
    macroActions,
    parsedDraft,
    flushUpdateBaseDraft,
    handleViewScheduledMessage,
  ]);

  const handleDoneViewingScheduledMessageSafe = useCallback(() => {
    const snapshot = buildDraftJson(editorRef.current, macroActions, parsedDraft);
    lastAppliedRef.current = snapshot;
    flushUpdateScheduledDraft(snapshot); // save scheduled before switching back
    handleDoneViewingScheduledMessage();
  }, [
    macroActions,
    parsedDraft,
    flushUpdateScheduledDraft,
    handleDoneViewingScheduledMessage,
  ]);

  const handleSendOptionSelectGuarded = useCallback(
    (optionId: string): Promise<void> | void => {
      if (hasScheduledMessage && !isViewingScheduledMessage) {
        handleViewScheduledMessageSafe();
      }
      return handleSendOptionSelect(optionId);
    },
    [
      handleSendOptionSelect,
      handleViewScheduledMessageSafe,
      hasScheduledMessage,
      isViewingScheduledMessage,
    ],
  );

  const renderPrimaryAction = useCallback(
    ({
      isLoading,
      disabled,
      buttonText,
      buttonRef,
      onClick,
      startIcon,
    }: {
      isLoading: boolean;
      disabled?: boolean;
      buttonText?: string;
      buttonRef: MutableRefObject<HTMLButtonElement | null>;
      onClick: () => void;
      startIcon?: string;
    }) => {
      rteSubmitButtonRef.current = buttonRef;
      sendNowHandlerRef.current = onClick;

      const shouldDisable = Boolean(
        disabled || isApplyingSendOption || isMessageEmpty || isViewingScheduledMessage,
      );

      const onOpenOptionsGuard = () => {
        if (hasScheduledMessage && !isViewingScheduledMessage) {
          setIsReplaceMode(false);
          setIsScheduleConflictOpen(true);
          return;
        }
        openScheduleModal();
      };

      return (
        <SendOptionsButton
          buttonText={buttonText}
          buttonRef={buttonRef}
          isLoading={isLoading || isApplyingSendOption}
          disabled={shouldDisable}
          onSend={onClick}
          startIcon={startIcon}
          onOpenOptions={onOpenOptionsGuard}
          isInternalNote={isInternalNote}
        />
      );
    },
    // eslint-disable-next-line
    [
      handleViewScheduledMessageSafe,
      hasScheduledMessage,
      isApplyingSendOption,
      isInternalNote,
      isMessageEmpty,
      isViewingScheduledMessage,
      openScheduleModal,
    ],
  );

  useEffect(() => {
    window.addEventListener('online', connect);
    window.addEventListener('offline', disconnect);

    function connect() {
      setIsOffline(false);
    }

    function disconnect() {
      setIsOffline(true);
    }

    return () => {
      window.removeEventListener('online', connect);
      window.removeEventListener('offline', disconnect);
    };
  }, []);

  useDraftCommitGuards(flushDraftNow);

  // A failed refine keeps the previous draft in the editor, so the pending bar
  // stands down while the failure is on screen rather than offering accept and
  // refine for a draft the agent has just been told did not update.
  const showAiSuggestion = !!pendingAiSuggestion && !aiGenerationError;

  return (
    <Box sx={inputFieldRootSx} onKeyDown={handleRootKeyDown}>
      <PendingAiSuggestionHeader
        promptName={selectedPromptName}
        show={showAiSuggestion}
        onDismiss={handleDismissAiSuggestion}
        onRefine={handleRefineSuggestion}
        onAccept={handleAcceptAiSuggestion}
      />
      <AiGenerationErrorBar
        error={aiGenerationError}
        sourceName={selectedPromptName}
        onRetry={retryGeneration}
        onDismiss={dismissAiGenerationError}
      />
      {!isNullish(conversation.partnerId) && (
        <HolidayBanner partnerId={conversation.partnerId} />
      )}
      <InputFieldBackground
        isInternalNote={isInternalNote}
        hasBannerAbove={hasAnyBanner || showAiSuggestion}
      >
        {isGeneratingAiSuggestion ? <SuggestionGenerationLoader /> : null}

        {suggestedReply && !isViewingScheduledMessage ? (
          <Stack spacing={1} mb={1}>
            <SuggestedReplyBanner
              sourceLabel={suggestedReply.source?.label}
              state={suggestedReplyBannerState}
              onView={viewSuggestedReply}
              onDismiss={handleDismissSuggestedReply}
              onRestorePersonalDraft={restorePersonalDraftBehindSuggestion ?? undefined}
            />
          </Stack>
        ) : null}

        {hasSendableScheduledMessage || isViewingScheduledMessage || isScheduleDiscarded ? (
          <Stack spacing={1} mb={1}>
            <ScheduledBanner
              scheduledLabel={scheduledLabel}
              onView={handleViewScheduledMessageSafe}
              onReschedule={handleReschedule}
              onDiscard={handleDiscardScheduledMessage}
              isSaving={isScheduledDraftSaving}
              conditions={scheduledConditions}
              isEditing={isViewingScheduledMessage}
              onDoneEditing={handleDoneViewingScheduledMessageSafe}
              status={isScheduleDiscarded ? 'discarded' : 'scheduled'}
              discardedLabel={scheduledDiscardedLabel}
              attachmentCount={scheduledServerAttachments.length}
            />
          </Stack>
        ) : null}

        <MessageTypeSelectorBackground isMobile={isMobile}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
          >
            <MessageTypeSelector
              disabled={isGeneratingAiSuggestion}
              options={messageTypeOptions}
              selectedValue={
                isInternalNote
                  ? ConversationChannelTypeExtended.InternalNote
                  : ConversationChannelTypeExtended.Chat
              }
              onMessageTypeChange={handleMessageTypeChange}
            />
            <Stack direction="row" alignItems="center" gap={1}>
              {!isInternalNote && showSmsSwitch ? (
                <SmsChannelSwitch
                  mode={smsMode}
                  onChange={setSmsMode}
                  smsEnabled={!smsDisabledReason}
                  smsDisabledReason={smsDisabledReason}
                />
              ) : null}
              {onCollapse ? (
                <IconButton
                  size="small"
                  aria-label="Hide composer"
                  title="Hide composer"
                  onClick={onCollapse}
                  sx={collapseButtonSx}
                  data-testid="composer-collapse"
                >
                  <DynamicIcon name="x" library="lucide" size={COLLAPSE_ICON_SIZE} />
                </IconButton>
              ) : null}
            </Stack>
          </Stack>
          {!isInternalNote && smsMode !== 'sms' && (
            <ParticipationPanel
              conversation={conversation}
              selectedParticipants={selectedParticipants}
              toIsModified={toIsModified}
              getToParticipant={getToParticipant}
              removeParticipant={removeParticipant}
              setToParticipant={setToParticipant}
              {...selectedParticipantsCallbacks}
            />
          )}
          {!isInternalNote && smsMode === 'sms' ? (
            <SmsLiveRecipientLine
              clientFirstName={conversation.requesterData?.firstName}
              hasOpenSession={hasOpenSession}
            />
          ) : null}
        </MessageTypeSelectorBackground>

        <Suspense>
          <RichTextEditor
            disabled={isOffline}
            size="small"
            toolbarIconOverrides={COMPOSER_TOOLBAR_ICON_OVERRIDES}
            pinnedFormattingMethods={COMPOSER_PINNED_FORMATTING_METHODS}
            renderFormatToggleIcon={(isExpanded) => (
              <ComposerFormatToggleIcon isExpanded={isExpanded} />
            )}
            fieldAutoSize={fieldAutoSize}
            fieldMinHeight={fieldMinHeight}
            content={parsedDraft ? stripMeta(parsedDraft) : activeRawDraft}
            onSubmit={onSendMessage}
            editorRef={editorRef}
            editorOptions={editorOptions}
            onBlur={handleEditorBlur}
            placeholderOptions={placeholderOptions}
            toolbarOptions={[
              <MacrosTrigger
                key="macros-trigger"
                onMacroSelected={onMacroSelected}
                conversation={conversation}
                isShortcutEnabled={!isFolded}
              />,
              <AiAssistantTrigger
                key="ai-assistant-trigger"
                onSubmit={(promptId?: string, promptName?: string, customPrompt?: string) =>
                  getInitialSuggestion({
                    promptId,
                    promptName,
                    customPrompt,
                    userId: user?.userId,
                    userInput: editorRef.current?.getHTML() || '',
                  })
                }
              />,
            ]}
            buttonText={getSendButtonText({ isInternalNote, smsMode, showSmsSwitch })}
            secondaryButtonText="Snooze"
            secondaryButtonTooltipText={
              <ShortcutText
                label="Snooze"
                shortcut={appShortcuts.openSnooze.command}
                size="small"
              />
            }
            secondaryButtonIcon="alarm-clock"
            secondaryButtonIconLibrary="lucide"
            handleSecondaryButton={showSnoozeModal}
            watchContent
            files={isViewingScheduledMessage ? scheduledFiles : files}
            setFiles={isViewingScheduledMessage ? setScheduledFiles : setFiles}
            slots={{
              contentFooter: (
                <AppliedMacroActionsList
                  onActionDelete={canDeleteMacroActions ? onActionDelete : undefined}
                  onReset={canDeleteMacroActions ? handleResetMacroActions : undefined}
                  actions={macroActions}
                  customChips={customActionChips}
                  partnerId={conversation.partnerId}
                />
              ),
            }}
            onGetMentionPopup={handleGetMentionPopup}
            renderPrimaryAction={renderPrimaryAction}
          />
        </Suspense>
      </InputFieldBackground>
      <SmartSendModal
        open={isScheduleConflictOpen || isScheduleModalOpen}
        conflict={
          isScheduleConflictOpen
            ? {
                scheduledLabel,
                previewText: buildPreviewFromJson(scheduledDraftContentRef.current || ''),
                onEditExisting: () => {
                  setIsReplaceMode(false);
                  handleViewScheduledMessageSafe();
                },
                onReplaceWithNew: () => setIsReplaceMode(true),
              }
            : undefined
        }
        initialDate={isReplaceMode ? null : (scheduledDraftSchedule?.scheduledAt ?? null)}
        initialConditions={scheduledConditions}
        sendOptions={sendOptions}
        onClose={() => {
          setIsScheduleConflictOpen(false);
          setIsReplaceMode(false);
          closeScheduleModal();
        }}
        onSubmit={(p) => {
          const res = handleScheduleModalSubmit({
            ...p,
            mode: isReplaceMode ? 'replace' : 'edit',
          });
          setIsReplaceMode(false);
          return res;
        }}
        onSelectOption={handleSendOptionSelectGuarded}
        isProcessingOption={isApplyingSendOption}
      />
    </Box>
  );
});

function needToChangeRequester(
  to: SelectedParticipants['to'],
  selectedRequester: PreparedConversationParticipantOption | null,
): boolean {
  if (!to) return false;
  if (!selectedRequester) return true;
  return (
    to.participantEntity !== selectedRequester.participantEntity ||
    to.participantIdentity !== selectedRequester.id
  );
}

function getSetRequesterChip(toRecipient: Required<SelectedParticipants>['to']): CustomChip {
  const name = toRecipient.name || toRecipient.participantIdentity;
  return {
    id: 'set-requester',
    icon: <ActionChipIcon name="person" />,
    label: name,
    tooltip: `Update requester to ${name}`,
  };
}

function getRequesterPlaceholder(
  toParticipant: SelectedParticipants['to'],
): PreparedConversationParticipantOption {
  return {
    id: toParticipant!.participantIdentity,
    value: '',
    label: toParticipant!.name || toParticipant!.participantIdentity,
    group: '',
    participantEntity: toParticipant!.participantEntity,
    conversationParticipantId: '',
    phoneNumber: '',
    email: '',
    avatarFileKey: null,
  };
}

function resolveStatusAction(
  value: string | null | undefined,
): { status: ConversationStatus; label: string } | null {
  if (!value) return null;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;

  const mapping: Record<string, { status: ConversationStatus; label: string }> = {
    solved: { status: ConversationStatus.Solved, label: 'conversation solved' },
    open: { status: ConversationStatus.Open, label: 'conversation reopened' },
    closed: { status: ConversationStatus.Closed, label: 'conversation closed' },
    new: { status: ConversationStatus.New, label: 'conversation marked as new' },
    on_hold: { status: ConversationStatus.OnHold, label: 'conversation placed on hold' },
  };

  return mapping[normalized] ?? null;
}

function buildPreviewFromJson(raw: string, lineLimit = 3): string {
  if (!raw) return '';
  try {
    const html = renderRichTextDocumentToHtml(raw);
    const text = (htmlToPlainText(html) || '').trim();
    if (!text) return '';
    const lines = text.split(/\s*\n+\s*|(?<=\.)\s+/).filter(Boolean); // split by newlines or sentence breaks
    const limited = lines.slice(0, lineLimit).join('\n');
    return limited.length > 500 ? `${limited.slice(0, 500)}…` : limited;
  } catch {
    const text = (htmlToPlainText(raw) || '').trim();
    return text.length > 500 ? `${text.slice(0, 500)}…` : text;
  }
}

function buildSmsParticipant(
  participation: ConversationParticipation,
  knownParticipants: ConversationParticipantItem[],
): SubmitConversationDataParticipant {
  const { channel } = participation;

  const smsChannel: SubmitConversationDataChannel = {
    channelType: ConversationChannelType.TwilioSMS,
    externalIdentifier: channel.externalIdentifier,
    internalIdentifier: channel.internalIdentifier,
    participationMode: ConversationParticipationMode.Engage,
    useToSendMessage: false,
  };

  const matched = knownParticipants.find(
    (p) => p.conversationParticipantId === channel.conversationParticipantId,
  );

  return {
    entity: (matched?.participantEntity ??
      ConversationParticipantEntity.Client) as ConversationParticipantEntity,
    identity: matched?.participantIdentity ?? '',
    channels: [smsChannel],
    // Holds because the caller resolved this participation from the selected To (or from the
    // conversation's single SMS channel) — never from an arbitrary SMS-capable participant.
    isRequester: true,
  };
}
